import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { isSiteCanadian } from 'src/utils/helpers';
import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import '../styles.scss';

interface DatePickerProps {
    value?: string | null;
    handleOnClose: (startDate: string) => void;
    totalPastYearsDisplayValue?: number;
}

const parseYMDToLocalDate = (value?: string | null): Date | undefined => {
    if (!value || typeof value !== 'string') return undefined;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return undefined;

    const year = Number(match[1]);
    const month = Number(match[2]) - Number('1');
    const day = Number(match[3]);

    return new Date(year, month, day);
};

const formatLocalDateToYMD = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + Number('1')}`.padStart(Number('2'), '0');
    const day = `${date.getDate()}`.padStart(Number('2'), '0');

    return `${year}-${month}-${day}`;
};

export const DatePickerSingle: React.FC<DatePickerProps> = ({
    value,
    handleOnClose,
    totalPastYearsDisplayValue,
}) => {
    const fp1 = useRef() as MutableRefObject<Instance | null>;
    const inputElementRef = useRef<HTMLInputElement | null>(null);

    const maxDate = new Date();
    const minDate = new Date();
    const priorYearValue = totalPastYearsDisplayValue || Number('1');

    minDate.setFullYear(maxDate.getFullYear() - priorYearValue);

    const inputRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) return;

            const input = node.querySelector(
                '[data-input]',
            ) as HTMLInputElement | null;

            inputElementRef.current = input;

            if (fp1.current) {
                fp1.current.destroy();
                fp1.current = null;
            }

            const initialDate = parseYMDToLocalDate(value);

            fp1.current = flatpickr(node, {
                wrap: true,
                static: false,
                clickOpens: true,
                allowInput: true,
                appendTo: document.body,
                positionElement: input || undefined,
                dateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',
                ariaDateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',
                mode: 'single',
                nextArrow:
                    '<i class="c-icon c-icon--[semantic-forward] c-icon--simple"></i>',
                prevArrow:
                    '<i class="c-icon c-icon--[semantic-back] c-icon--simple"></i>',
                minDate,
                maxDate,
                monthSelectorType: 'static',
                defaultDate: initialDate,
                onClose: (selectedDates) => {
                    if (!selectedDates?.[0]) {
                        handleOnClose('');
                        return;
                    }

                    handleOnClose(formatLocalDateToYMD(selectedDates[0]));
                },
            });

            if (initialDate) {
                fp1.current.setDate(initialDate, false);
            } else {
                fp1.current.clear();
                if (inputElementRef.current) {
                    inputElementRef.current.value = '';
                }
            }
        },
        [handleOnClose, totalPastYearsDisplayValue, value],
    );

    useEffect(() => {
        if (!fp1.current) return;

        const parsedDate = parseYMDToLocalDate(value);

        if (parsedDate) {
            fp1.current.setDate(parsedDate, false);
        } else {
            fp1.current.clear();
            if (inputElementRef.current) {
                inputElementRef.current.value = '';
            }
        }
    }, [value]);

    useEffect(() => {
        return () => {
            if (fp1.current) {
                fp1.current.destroy();
                fp1.current = null;
            }
        };
    }, []);

    return (
        <div
            className='c-datepicker js-datepicker date-picker'
            data-datepicker-month-selector-type='static'
        >
            <div className='c-input' ref={inputRef}>
                <input
                    className='c-input__input'
                    id='datepicker'
                    type='text'
                    data-input
                    placeholder='Select Date'
                />
                <button
                    className='c-input__addon c-input__addon--no-background'
                    tabIndex={-1}
                    aria-label='Toggle Datepicker'
                    type='button'
                    data-toggle
                >
                    <i
                        className='c-icon c-icon--[semantic-calendar]'
                        aria-hidden='true'
                        role='img'
                    />
                </button>
                <button
                    className='c-input__addon c-input__addon--no-background'
                    type='button'
                    aria-label='Clear Datepicker'
                    onClick={() => {
                        fp1.current?.clear();
                        if (inputElementRef.current) {
                            inputElementRef.current.value = '';
                        }
                        handleOnClose('');
                    }}
                >
                    X
                </button>
            </div>
        </div>
    );
};
